import { useCallback, useState } from 'react';

/**
 * Preload a list of image URLs into the browser cache and report when all of
 * them have settled.
 *
 * NOTE: previously each image preload was a Promise that rejected on
 * `img.onerror`, fed into `Promise.all` whose `.catch` only observes the FIRST
 * failure — every additional failure was an unhandled rejection. With user
 * feed cards loading multiple photos and 404 / deleted photos being routine,
 * this generated hundreds of thousands of "Non-Error promise rejection" events
 * in Sentry (issue #52716959, ~454k events / 13.7k users since 2025-07).
 *
 * Now each preload always resolves (image-load failure is an expected
 * outcome, not an exception) so no rejection can leak. The boolean return
 * semantic is preserved: `imagesPreloaded` becomes true only when every
 * image actually loaded.
 */
type PreloadOutcome = { url: string; ok: boolean };

export const usePreloadImages = () => {
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  const triggerPreload = useCallback((imageUrls: string[]) => {
    const promises: Promise<PreloadOutcome>[] = imageUrls.map(
      (url) =>
        new Promise<PreloadOutcome>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ url, ok: true });
          img.onerror = () => resolve({ url, ok: false });
          img.src = url;
        }),
    );

    void Promise.all(promises).then((results) => {
      setImagesPreloaded(results.every((r) => r.ok));
    });
  }, []);

  return { preloadImages: triggerPreload, imagesPreloaded };
};
